import type { Theme } from '@mui/joy/styles';

// ── Homepage launcher card ─────────────────────────────────────────────────
export const launcherCardSx = (t: Theme) => ({
  position: 'fixed' as const,
  bottom: 24,
  left: 24,
  width: 427,
  zIndex: 9000,
  p: 3,
  gap: 2,
  padding: '36px 33px 36px 33px',
  borderRadius: '14px',
  boxShadow: 'lg',
  bgcolor: t.palette.background.surface,
  borderColor: t.palette['expert']['light'],
  animation: 'homepageLauncherSlideIn 0.45s cubic-bezier(0.2, 0.8, 0.2, 1)',
  '@keyframes homepageLauncherSlideIn': {
    from: { transform: 'translateY(20px)', opacity: 0 },
    to: { transform: 'translateY(0)', opacity: 1 },
  },
});

export const launcherChipSx = {
  color: (theme: Theme) => theme.palette.primary.solidColor,
  background: (theme: Theme) => theme.palette['expert']['light'],
};

export const launcherCtaButtonSx = {
  background: (theme: Theme) => theme.palette['expert']['light'],
};

// ── Tour popover ───────────────────────────────────────────────────────────
export const popoverContainerSx = (t: Theme) => ({
  bgcolor: t.palette.background.surface,
  boxShadow: 'lg',
  borderRadius: '10px',
  p: '20px',
  gap: 2,
  width: 360,
  boxSizing: 'border-box' as const,
});

// ── Tour overlay theme (injected once into <head>) ─────────────────────────
export const TOUR_THEME_CSS = `
    /* Queue tour backdrop: a subtle blur behind the dark dim (Figma 363:46536,
       BACKGROUND_BLUR r4). Scoped to the queue tour via the <html> class so the
       element-highlighting home/unrated tours keep a crisp, un-blurred overlay. */
    html.tour-overlay-blur .driver-overlay {
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
    }
    .driver-fade .driver-popover.driverjs-onboarding-theme { animation: none !important; }
    /* driver.js's auto-positioned arrow is unreliable when floating-ui shifts
       the popover; we draw our own triangle (.calyx-tour-arrow) anchored to
       the popover edge facing the highlight. */
    .driverjs-onboarding-theme .driver-popover-arrow { display: none !important; }
    .calyx-tour-arrow {
      position: fixed;
      width: 0;
      height: 0;
      border: 10px solid transparent;
      z-index: 100000;
      pointer-events: none;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
    }
    .calyx-tour-arrow[data-side="left"]   {
      border-right-color: var(--joy-palette-background-surface, #ffffff);
      transform: translate(-100%, -50%);
    }
    .calyx-tour-arrow[data-side="right"]  {
      border-left-color: var(--joy-palette-background-surface, #ffffff);
      transform: translate(0%, -50%);
    }
    .calyx-tour-arrow[data-side="top"]    {
      border-bottom-color: var(--joy-palette-background-surface, #ffffff);
      transform: translate(-50%, -100%);
    }
    .calyx-tour-arrow[data-side="bottom"] {
      border-top-color: var(--joy-palette-background-surface, #ffffff);
      transform: translate(-50%, 0%);
    }
    .calyx-tour-dot {
      position: fixed;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #127C16;
      pointer-events: none;
      animation: calyxTourPulse 1.4s ease-out infinite;
      z-index: 100001;
      transform: translate(-50%, -50%);
    }
    /* driver.js kills pointer events on everything but its active element and
       that element's children. A spotlight step highlights a proxy, so the real
       targets have to be re-enabled or they look lit but ignore hover. */
    .driver-active .onboarding-spotlit,
    .driver-active .onboarding-spotlit * {
      pointer-events: auto;
    }
    @keyframes calyxTourPulse {
      0%   { box-shadow: 0 0 0 0    rgba(18, 124, 22, 0.5); }
      70%  { box-shadow: 0 0 0 14px rgba(18, 124, 22, 0);   }
      100% { box-shadow: 0 0 0 0    rgba(18, 124, 22, 0);   }
    }
  `;
